import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import useSetting from '@/hooks/setting';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, Settings2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";

const formSchema = z.object({
    key: z.string().min(1, "Key is required"),
    value: z.string().min(1, "Value is required"),
    description: z.string().optional(),
});

const UpdateSetting = () => {
    const navigate = useNavigate();
    const { key } = useParams();
    const { settings, updateSetting } = useSetting();

    const currentSetting = settings?.find(setting => setting.key === key);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            key: currentSetting?.key || "",
            value: currentSetting?.value?.toString() || "",
            description: currentSetting?.description || "",
        },
    });

    useEffect(() => {
        if (currentSetting) {
            form.reset({
                key: currentSetting.key,
                value: currentSetting.value?.toString() || "",
                description: currentSetting.description || "",
            });
        }
    }, [currentSetting, form]);

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        try {
            await updateSetting.mutateAsync({
                key: values.key,
                value: values.value,
                description: values.description,
            });
            navigate('/setting/list-setting');
        } catch (error) {
            console.error("Error submitting form:", error);
        }
    };

    if (!currentSetting) {
        return <div>Setting not found</div>;
    }

    return (
        <div className="container max-w-4xl py-6">
            <Card>
                <CardHeader className="space-y-1">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate('/setting/list-setting')}
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <div className="flex items-center gap-2">
                            <Settings2 className="h-5 w-5" />
                            <div>
                                <CardTitle className="text-2xl font-bold">Update Setting</CardTitle>
                                <CardDescription>
                                    Update an existing setting in the system
                                </CardDescription>
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <Separator />
                <CardContent className="pt-6">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                            <div className="grid gap-6">
                                <FormField
                                    control={form.control}
                                    name="key"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-base">Key</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    disabled
                                                    className="h-10"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="value"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-base">Value</FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    {...field}
                                                    rows={4}
                                                    placeholder="Enter value"
                                                    className="resize-y"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="description"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-base">Description</FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    {...field}
                                                    rows={2}
                                                    placeholder="Enter description (optional)"
                                                    className="resize-y"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className="flex gap-4 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="flex-1 h-11"
                                    onClick={() => navigate('/setting/list-setting')}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="flex-1 h-11"
                                    disabled={updateSetting.isPending}
                                >
                                    {updateSetting.isPending ? "Updating..." : "Update"}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
};


export default UpdateSetting; 